import React, { useMemo, useRef, useState } from 'react'
import {
  Box,
  Button,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
  VStack
} from '@chakra-ui/react'
import * as icons from '@heroicons/react/24/outline'

const ICON_NAMES = Object.keys(icons)
  .filter((name) => name.endsWith('Icon'))
  .sort()

type IconPickerProps = {
  value: string | null
  onChange: (name: string | null) => void
}

export function IconPicker({ value, onChange }: IconPickerProps) {
  const [query, setQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const filteredIcons = useMemo(
    () =>
      ICON_NAMES.filter((name) => name.toLowerCase().includes(query.toLowerCase())).slice(0, 50),
    [query]
  )
  const SelectedIcon = value ? (icons as any)[value] : null

  const closePicker = () => setIsOpen(false)
  const openPicker = () => setIsOpen(true)

  const keepInputFocused = (event: React.MouseEvent) => {
    event.preventDefault()
  }

  const focusInput = () => {
    requestAnimationFrame(() => inputRef.current?.focus())
  }

  return (
    <Box
      position='relative'
      onBlur={(event: React.FocusEvent<HTMLDivElement>) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          closePicker()
        }
      }}
    >
      <InputGroup>
        {SelectedIcon && (
          <InputLeftElement pointerEvents='none'>
            <SelectedIcon className='h-4 w-4' />
          </InputLeftElement>
        )}
        <Input
          ref={inputRef}
          pl={SelectedIcon ? 10 : undefined}
          value={query}
          placeholder={value ?? 'Search icons...'}
          onChange={(e) => {
            setQuery(e.target.value)
            openPicker()
          }}
          onFocus={openPicker}
          onClick={openPicker}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              closePicker()
            }
            if (event.key === 'Enter' && filteredIcons[0]) {
              event.preventDefault()
              onChange(filteredIcons[0])
              setQuery('')
              closePicker()
            }
          }}
        />
      </InputGroup>
      {isOpen && (
        <Box
          bg='white'
          borderRadius='md'
          borderWidth='1px'
          boxShadow='lg'
          mt={1}
          p={2}
          position='absolute'
          width='100%'
          zIndex='popover'
        >
          <VStack align='stretch' maxH='300px' overflowY='auto' spacing={0}>
            {filteredIcons.map((name) => {
              const Icon = (icons as any)[name]
              return (
                <HStack
                  key={name}
                  cursor='pointer'
                  px={2}
                  py={1.5}
                  rounded='md'
                  _hover={{ bg: 'gray.100' }}
                  onMouseDown={keepInputFocused}
                  onClick={() => {
                    onChange(name)
                    setQuery('')
                    closePicker()
                    focusInput()
                  }}
                >
                  <Icon className='h-4 w-4' />
                  <Text>{name}</Text>
                </HStack>
              )
            })}
            {filteredIcons.length === 0 && (
              <Box px={2} py={1.5}>
                <Text color='gray.500'>No icons found</Text>
              </Box>
            )}
          </VStack>
          <Button
            mt={2}
            size='sm'
            variant='ghost'
            onMouseDown={keepInputFocused}
            onClick={() => {
              onChange(null)
              setQuery('')
              closePicker()
              focusInput()
            }}
          >
            Clear
          </Button>
        </Box>
      )}
    </Box>
  )
}
